import { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';

/**
 * QR platba SPD 1.0 — podpora vývoje.
 * Port z frontend/index.html:7488 (footer-qr) a :7501 (landing-qr).
 * Účet Air Bank 1245278013/3030 → IBAN CZ07 3030 0000 0012 4527 8013.
 */

const ACCOUNT = '1245278013/3030';
const SPD_PAYLOAD = 'SPD*1.0*ACC:CZ0730300000001245278013*MSG:Podpora-Stavba-Web*';

export interface SupportQRProps {
  size?: number;
  /** Titulek vedle QR. `null` pro jen QR. */
  variant?: 'footer' | 'landing' | 'compact';
}

/** Tlačítko „Zkopírovat číslo účtu" s krátkou zpětnou vazbou — pro mobil, kde se QR neskenuje. */
export function CopyAccount({
  dark = false,
  justify = 'start',
  compact = false,
}: {
  dark?: boolean;
  justify?: 'start' | 'end';
  compact?: boolean;
}) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(ACCOUNT);
    } catch {
      // Starší prohlížeče / nedostupný clipboard API — fallback přes dočasné pole.
      const ta = document.createElement('textarea');
      ta.value = ACCOUNT;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
      } catch {
        /* vzdáme to tiše — číslo je vidět vedle */
      }
      document.body.removeChild(ta);
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div
      style={{
        marginTop: 6,
        display: 'flex',
        alignItems: 'center',
        justifyContent: justify === 'end' ? 'flex-end' : 'flex-start',
        gap: 8,
        flexWrap: 'wrap',
      }}
    >
      <span
        style={{
          fontFamily: 'var(--sd-font-mono, ui-monospace, monospace)',
          fontSize: compact ? 10 : 12,
          fontWeight: 600,
          color: dark ? 'var(--sd-text)' : 'inherit',
        }}
      >
        {ACCOUNT}
      </span>
      <button
        type="button"
        onClick={copy}
        style={{
          cursor: 'pointer',
          fontSize: compact ? 9.5 : 11,
          fontWeight: 600,
          padding: compact ? '2px 7px' : '3px 9px',
          borderRadius: 6,
          border: dark ? '1px solid rgba(255,255,255,0.22)' : '1px solid var(--sd-border, #d0d5dd)',
          background: copied
            ? 'var(--sd-success-soft, rgba(34,160,90,0.15))'
            : dark
              ? 'rgba(255,255,255,0.08)'
              : 'var(--sd-surface-2, #f2f4f7)',
          color: copied ? 'var(--sd-success, #1a7f43)' : dark ? 'var(--sd-text)' : 'var(--sd-text, #1d2939)',
          transition: 'background 0.15s, color 0.15s',
          whiteSpace: 'nowrap',
        }}
      >
        {copied ? '✓ Zkopírováno' : compact ? '📋 Kopírovat účet' : '📋 Zkopírovat číslo účtu'}
      </button>
    </div>
  );
}

export function SupportQR({ size = 88, variant = 'footer' }: SupportQRProps) {
  const qr = (
    <div
      style={{
        background: 'white',
        borderRadius: 6,
        padding: 4,
        lineHeight: 0,
        flexShrink: 0,
      }}
    >
      <QRCodeSVG value={SPD_PAYLOAD} size={size} level="M" fgColor="#0d2645" bgColor="#ffffff" />
    </div>
  );

  if (variant === 'compact') return qr;

  if (variant === 'landing') {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        {qr}
        <div style={{ fontSize: 11, color: 'var(--sd-text-dim)', maxWidth: 200, lineHeight: 1.4 }}>
          <div style={{ fontWeight: 600, color: 'var(--sd-text)' }}>☕ Podpora</div>
          Naskenujte v bankovní aplikaci — libovolnou částkou podpoříte vývoj. Z mobilu si jen zkopírujte
          číslo účtu.
          <CopyAccount />
        </div>
      </div>
    );
  }

  // footer
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 14,
        background: 'rgba(255,255,255,0.05)',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 10,
        padding: '12px 16px',
      }}
    >
      {qr}
      <div style={{ maxWidth: 240 }}>
        <div
          style={{
            color: 'var(--sd-text)',
            fontWeight: 600,
            fontSize: 12,
            marginBottom: 5,
          }}
        >
          ☕ Podpořte vývoj
        </div>
        <div style={{ color: 'var(--sd-text-dim)', fontSize: 11, lineHeight: 1.55 }}>
          Ušetřila vám aplikace čas? Podpořte další vývoj drobným příspěvkem přes QR. Děkuji!
        </div>
        <CopyAccount dark />
      </div>
    </div>
  );
}
